import {
  block,
  checkForNewDialogs,
  getDialog,
  hide,
  showAll,
} from "@/api/repositories/dialogs";
import { defineStore } from "pinia";
import { ref } from "vue";
import { useAuthStore } from "./auth";
import bus from "@/bus";
import { useRouter } from "vue-router";
import { computed } from "vue";
import { DIALOGS_TTL } from "@/constants";

/**
 * Filters an array of objects by their IDs, returning a new array with only the unique objects.
 *
 * @param {Array} array - The array of objects to filter.
 * @return {Array} The filtered array of objects.
 */
const filterArrayById = (array) => {
  return array.reduce((acc, current) => {
    const x = acc.find((item) => item.id === current.id);
    if (!x) {
      return acc.concat([current]);
    } else {
      return acc;
    }
  }, []);
};

/**
 * Creates a minimal user object with only essential fields for caching
 *
 * @param {Object} user - The full user object
 * @return {Object} The minimal user object with essential fields only
 */
const createMinimalUser = (user) => {
  return {
    id: user.id,
    name: user.name,
    avatar: user.avatar,
    person_verification: user.person_verification,
    properties: user.properties?.filter((p) => p.category_name === "Sprinkle"),
  };
};

/**
 * Transforms dialogs to use minimal user data for caching
 *
 * @param {Array} dialogs - Array of dialog objects with full user data
 * @return {Array} Array of dialog objects with minimal user data
 */
const transformDialogsForCaching = (dialogs) => {
  return dialogs.map((dialog) => ({
    ...dialog,
    user: createMinimalUser(dialog.user),
  }));
};

export const useDialogsStore = defineStore(
  "dialogs",
  () => {
    const cachedDialogs = ref([]);
    const uncachedDialogs = ref([]);
    const dialogsLoading = ref(false);
    const dialogsCurrentPage = ref(1);
    const isDialogsListenerInitialized = ref(false);
    const activeDialogId = ref(undefined);
    const noMoreDialogs = ref(false);
    const authStore = useAuthStore();
    const router = useRouter();
    const fetchedDialogsTimestamp = ref(0);
    const dialogPageScroll = ref(0);
    const activeDialogParticipants = ref([]);

    const dialogs = computed(() => {
      return [...cachedDialogs.value, ...uncachedDialogs.value].sort((a, b) => {
        // Safely access last_message in case it's null
        const aTime = a.last_message?.created_at
          ? new Date(a.last_message.created_at).getTime()
          : 0;
        const bTime = b.last_message?.created_at
          ? new Date(b.last_message.created_at).getTime()
          : 0;
        return bTime - aTime;
      });
    });
    const getFirstPage = async () => {
      if (dialogsLoading.value === true) return;

      const { hasNew } = fetchedDialogsTimestamp.value
        ? await checkForNewDialogs(fetchedDialogsTimestamp.value)
        : { hasNew: true };
      if (
        !hasNew &&
        Date.now() - fetchedDialogsTimestamp.value * 1000 < DIALOGS_TTL
      ) {
        dialogsCurrentPage.value = 2;
        return;
      }

      uncachedDialogs.value = [];
      dialogsCurrentPage.value = 1;
      noMoreDialogs.value = false;
      dialogsLoading.value = true;

      try {
        const { dialogs: newDialogs, hasMorePages } = await showAll(1);
        cachedDialogs.value = filterArrayById(
          transformDialogsForCaching(newDialogs)
        );
        noMoreDialogs.value = !hasMorePages;
        dialogsCurrentPage.value = 2; // Set to 2 since we'll start fetching from page 2
        fetchedDialogsTimestamp.value = Math.floor(Date.now() / 1000);
      } catch (error) {
        fetchedDialogsTimestamp.value = 0;
        noMoreDialogs.value = true;
        throw error;
      } finally {
        dialogsLoading.value = false;
      }
    };

    const getNextPage = async () => {
      if (
        dialogsLoading.value === true ||
        noMoreDialogs.value ||
        dialogsCurrentPage.value === 1
      ) {
        return;
      }
      dialogsLoading.value = true;

      try {
        const { dialogs: newDialogs, hasMorePages } = await showAll(
          dialogsCurrentPage.value
        );
        uncachedDialogs.value = filterArrayById([
          ...uncachedDialogs.value,
          ...transformDialogsForCaching(newDialogs),
        ]);
        noMoreDialogs.value = !hasMorePages;
        dialogsCurrentPage.value++;
      } catch (error) {
        noMoreDialogs.value = true;
        throw error;
      } finally {
        dialogsLoading.value = false;
      }
    };

    const getDialogs = async (shouldRefresh = false) => {
      if (dialogsCurrentPage.value === 1) {
        await getFirstPage(shouldRefresh);
      } else {
        await getNextPage();
      }
    };

    const messagesRead = ({
      dialogId,
      unread_messages_count_for_sender,
      unread_messages_count_for_recipient,
    }) => {
      function setCurrentUnreadMessagesCount(dialog) {
        if (dialog.id === dialogId) {
          dialog.unread_messages_count_for_sender =
            unread_messages_count_for_sender;
          dialog.unread_messages_count_for_recipient =
            unread_messages_count_for_recipient;
        }
        return dialog;
      }
      cachedDialogs.value = cachedDialogs.value.map(
        setCurrentUnreadMessagesCount
      );
      uncachedDialogs.value = uncachedDialogs.value.map(
        setCurrentUnreadMessagesCount
      );
    };

    const updateDialog = (dialog) => {
      if (activeDialogId.value === dialog.id) {
        if (dialog.blocked_by_user_id) {
          deactivateDialog();
        }
      }

      // if the dialog is not in the list we add it to the beginning
      if (
        !dialogs.value.find((item) => item.id === dialog.id) &&
        dialog.blocked_by_user_id !== authStore.user.id
      ) {
        const user =
          dialog.from.id === authStore.user.id ? dialog.to : dialog.from;
        cachedDialogs.value.unshift({
          ...dialog,
          user: createMinimalUser(user),
        });
        return;
      }

      // if the dialog is in the uncached list we update it and move it to the beginning(cachedDialogs)
      const existingUncachedDialog = uncachedDialogs.value.find(
        (item) => item.id === dialog.id
      );
      if (
        existingUncachedDialog &&
        existingUncachedDialog.last_message?.created_at !==
          dialog.last_message?.created_at
      ) {
        uncachedDialogs.value = uncachedDialogs.value.filter(
          (d) => d.id !== dialog.id
        );
        cachedDialogs.value.unshift({
          ...dialog,
          user: existingUncachedDialog.user,
        });
        uncachedDialogs.value.unshift(cachedDialogs.value.pop());
        return;
      }
      // updating dialog in case it's already in cachedDialogs
      cachedDialogs.value = cachedDialogs.value.map((chat) => {
        return chat.id === dialog.id
          ? { ...dialog, user: chat.user } // not overriding profile cause it can store the opposite user
          : chat;
      });
    };

    const blockDialog = async (dialog) => {
      await block(dialog.id);
      cachedDialogs.value = cachedDialogs.value.filter(
        (item) => item.id !== dialog.id
      );
    };

    const blockDialogByUserId = async (userId) => {
      const dialog = findDialogByUserId(userId);
      if (dialog) {
        await blockDialog(dialog);
      }
    };

    const removeDialog = async (dialog) => {
      await hide(dialog.id);
      cachedDialogs.value = cachedDialogs.value.filter(
        (item) => item.id !== dialog.id
      );
    };

    const activateDialog = (dialog) => {
      activeDialogId.value = dialog.id;
      getDialog(dialog.id).then(({ dialog: updatedDialog }) => {
        updateDialog(updatedDialog);
      });
    };

    const deactivateDialog = (skipRouteChange = false) => {
      if (skipRouteChange) {
        activeDialogId.value = undefined;
      } else {
        router.push({ name: "dialogs" }).then(() => {
          activeDialogId.value = undefined;
        });
        bus.global.emit("modal.bottom:close", {
          name: "chat",
        });
      }
    };

    const getDialogByUserId = async (userId) => {
      if (!cachedDialogs.value.length) {
        await getFirstPage();
      }
      return cachedDialogs.value.find((dialog) => {
        return dialog.user.id === userId;
      });
    };

    const findDialogByUserId = (userId) => {
      const cachedDialog = cachedDialogs.value.find((dialog) => {
        return dialog.from.id === userId || dialog.to.id === userId;
      });
      if (cachedDialog) return cachedDialog;

      return uncachedDialogs.value.find((dialog) => {
        return dialog.from.id === userId || dialog.to.id === userId;
      });
    };

    const resetDialogs = () => {
      cachedDialogs.value = [];
      activeDialogId.value = undefined;
      fetchedDialogsTimestamp.value = 0;
      dialogsCurrentPage.value = 1;
      uncachedDialogs.value = [];
      noMoreDialogs.value = false;
      dialogsLoading.value = false;
      dialogPageScroll.value = 0;
      isDialogsListenerInitialized.value = false;
      activeDialogParticipants.value = [];
    };

    const activeDialog = computed(() => {
      if (!activeDialogId.value) return null;
      return dialogs.value.find((dialog) => {
        return dialog.id === activeDialogId.value;
      });
    });

    const setDialogParticipants = (users) => {
      activeDialogParticipants.value = users.map((user) => Number(user.id));
    };

    const addDialogParticipant = (userId) => {
      activeDialogParticipants.value.push(Number(userId));
    };

    const removeDialogParticipant = (userId) => {
      activeDialogParticipants.value = activeDialogParticipants.value.filter(
        (id) => id !== userId
      );
    };

    const isInDialogWithYou = computed(
      () => activeDialogParticipants.value.length === 2
    );
    const isVideoCallAvailable = computed(
      () => activeDialog.value && isInDialogWithYou.value
    );

    return {
      dialogs,
      cachedDialogs,
      uncachedDialogs,
      activeDialogId,
      activeDialog,
      activeDialogParticipants,
      dialogsLoading,
      getDialogs,
      getFirstPage,
      getNextPage,
      getDialogByUserId,
      findDialogByUserId,
      updateDialog,
      dialogsCurrentPage,
      noMoreDialogs,
      blockDialog,
      blockDialogByUserId,
      removeDialog,
      isDialogsListenerInitialized,
      activateDialog,
      deactivateDialog,
      resetDialogs,
      dialogPageScroll,
      fetchedDialogsTimestamp,
      messagesRead,
      setDialogParticipants,
      removeDialogParticipant,
      addDialogParticipant,
      isInDialogWithYou,
      isVideoCallAvailable,
    };
  },
  {
    persist: {
      paths: ["cachedDialogs", "dialogPageScroll", "fetchedDialogsTimestamp"],
    },
  }
);
